import { faCommentQuestion, faThumbsDown, faThumbsUp } from '@fortawesome/pro-regular-svg-icons';
import { faQuestion } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import VoxelButton from '../../../../../components/voxel/Buttons/VoxelButton';
import { ContentTaskGq } from '../../../../../graphql/projects/models/ContentTaskGq';
import { ContentTaskStatusGq } from '../../../../../graphql/projects/models/ContentTaskStatusGq';
import { projectsApiService } from '../../../../../graphql/projects/projectsApiService';

export interface ApproveContentButtonProps {
  contentTask: ContentTaskGq;
  reload: () => Promise<any>;
  iconSize?: 'large' | 'small';
  table?: boolean;
  disabled: boolean;
}

const ApproveContentButton: React.FC<ApproveContentButtonProps> = ({
  reload,
  contentTask,
  table,
}) => {
  const [localApprovedStatus, setLocalApprovedStatus] = useState<ContentTaskStatusGq>(
    contentTask.approvedStatus
  );
  const [localApprovedDateTime, setLocalApprovedDateTime] = useState<string>(
    contentTask.approvedStatusDateTime
  );

  useEffect(() => {
    setLocalApprovedStatus(contentTask.approvedStatus);
  }, [contentTask.approvedStatus]);

  useEffect(() => {
    setLocalApprovedDateTime(contentTask.approvedStatusDateTime);
  }, [contentTask.approvedStatusDateTime]);

  const approveContentTaskMutation =
    projectsApiService.mutations.contentPlan.useApproveContentTask();

  const loading = localApprovedStatus != contentTask.approvedStatus;
  const handleApproveContentTask = useCallback(
    (event: React.MouseEvent<any>) => {
      event.stopPropagation();
      if (loading) return;

      // Local approved
      const choices = Object.values(ContentTaskStatusGq);
      const currentChoice = choices.findIndex((v) => v === localApprovedStatus);
      const approved = (currentChoice + 1) % choices.length;
      const enumApproved = choices[approved];
      setLocalApprovedStatus(enumApproved);

      // Local dateTime
      const today = new Date();
      const dateTimeNow =
        today.getFullYear() + '/' + (today.getMonth() + 1) + '/' + today.getDate();
      setLocalApprovedDateTime(dateTimeNow);

      approveContentTaskMutation({
        projectId: contentTask.projectId,
        contentTaskId: contentTask.id,
        approvedStatus: enumApproved,
      }).then(reload);
    },
    [reload, localApprovedStatus, contentTask.id, loading]
  );

  const approvedDate = moment.utc(localApprovedDateTime).format('YYYY-MM-DD');

  return (
    <div className="flex flex-row justify-center">
      {localApprovedStatus === ContentTaskStatusGq.UNDECIDED && (
        <VoxelButton
          size="sm"
          onClick={handleApproveContentTask}
          title={table ? approvedDate : ContentTaskStatusGq.UNDECIDED}
          endIcon={<FontAwesomeIcon icon={faQuestion} />}
          color="light-gray"
          disabled={loading}
          tooltip={!table ? approvedDate : ContentTaskStatusGq.UNDECIDED}
        />
      )}
      {localApprovedStatus === ContentTaskStatusGq.APPROVED && (
        <VoxelButton
          size="sm"
          color="success"
          onClick={handleApproveContentTask}
          title={table ? approvedDate : ContentTaskStatusGq.APPROVED}
          endIcon={<FontAwesomeIcon icon={faThumbsUp} />}
          disabled={loading}
          tooltip={!table ? approvedDate : ContentTaskStatusGq.APPROVED}
        />
      )}
      {localApprovedStatus === ContentTaskStatusGq.DISAPPROVED && (
        <VoxelButton
          size="sm"
          onClick={handleApproveContentTask}
          title={table ? approvedDate : ContentTaskStatusGq.DISAPPROVED}
          endIcon={<FontAwesomeIcon icon={faThumbsDown} />}
          color={'error'}
          disabled={loading}
          tooltip={!table ? approvedDate : ContentTaskStatusGq.DISAPPROVED}
        />
      )}
      {localApprovedStatus === ContentTaskStatusGq.PROPOSAL && (
        <VoxelButton
          size="sm"
          color="warning"
          onClick={handleApproveContentTask}
          title={table ? approvedDate : ContentTaskStatusGq.PROPOSAL}
          endIcon={<FontAwesomeIcon icon={faCommentQuestion} />}
          disabled={loading}
          tooltip={!table ? approvedDate : ContentTaskStatusGq.PROPOSAL}
        />
      )}
    </div>
  );
};

export default ApproveContentButton;
